import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, CircleCheck, Clock, ExternalLink, Eye, Landmark, Plus, RefreshCw, Unplug } from "lucide-react";
import { toast } from "sonner";
import { useDolfinClient } from "@/components/DolfinProvider";
import { ConnectBankDialog, bankConnectionReturnUrl } from "@/components/ConnectBankDialog";
import { DisclosureNotice } from "@/components/PayByBank";
import { ToggleChip } from "@/components/ToggleChip";
import { PageContainer, PageHeader } from "@/components/PagePrimitives";
import { EmptyState, ErrorState, Eyebrow, HeaderRow, LoadingState, TH, TableShell, rowLink } from "@/components/PageUi";
import { apiErrorMessage } from "@/lib/api-error";
import { formatCurrency, formatDate, formatDateTime } from "@/lib/format";
import { usePermissions } from "@/lib/permissions";
import { useIsDemo } from "@/demo/DemoContext";
import { cn } from "@/lib/utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import type { components } from "@dolfin/api-admin";

type BankConnection = components["schemas"]["BankConnectionResponse"];
type BankAccount = components["schemas"]["BankAccountResponse"];
type BankConnectionAuthorisation = components["schemas"]["BankConnectionAuthorisationResponse"];
type BankConnectionState = components["schemas"]["BankConnectionState"];
type FailureReason = components["schemas"]["BankConnectionFailureReason"];

// The backend reminds 7 days out; a fortnight leaves room to act without the button showing for all 90 days.
const RECONFIRM_WINDOW_DAYS = 14;

const TERMINAL: BankConnectionState[] = ["Failed", "Revoked", "Disconnected"];

export function bankConnectionStateLabel(state: BankConnectionState): string {
  switch (state) {
    case "Initiated":
      return "Starting";
    case "AwaitingAuthorisation":
      return "Waiting for your bank";
    case "AwaitingFurtherAuthorisation":
      return "Waiting for a second approver";
    case "Connected":
      return "Connected";
    case "Failed":
      return "Not connected";
    case "Expired":
      return "Expired";
    case "Reauthorising":
      return "Reauthorising";
    case "Revoked":
      return "Revoked at the bank";
    case "Disconnected":
      return "Disconnected";
  }
}

export function bankConnectionFailureLabel(reason: FailureReason | undefined): string | null {
  switch (reason) {
    case "UserRejected":
      return "Access was declined or cancelled at the bank.";
    case "BankError":
      return "The bank reported an error.";
    case "Abandoned":
      return "Authorisation was not completed within an hour.";
    case "InvalidConsent":
      return "The bank did not accept the consent.";
    case "Unknown":
      return "The connection could not be completed.";
    default:
      return null;
  }
}

export function BankConnectionStateBadge({ state }: { state: BankConnectionState }) {
  const tone =
    state === "Connected"
      ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
      : state === "Expired" || state === "Reauthorising" || state === "AwaitingFurtherAuthorisation"
        ? "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300"
        : state === "Failed" || state === "Revoked"
          ? "border-destructive/30 bg-destructive/10 text-destructive"
          : "";
  return (
    <Badge variant="outline" className={tone}>
      {bankConnectionStateLabel(state)}
    </Badge>
  );
}

function daysUntil(iso: string | null | undefined): number | null {
  if (!iso) return null;
  return Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000);
}

export function accountIdentifier(account: BankAccount): string {
  if (account.sortCode && account.accountNumber) {
    const sc = account.sortCode.replace(/\D/g, "");
    const formatted = sc.length === 6 ? `${sc.slice(0, 2)}-${sc.slice(2, 4)}-${sc.slice(4)}` : account.sortCode;
    return `${formatted}  ${account.accountNumber}`;
  }
  return account.iban ?? "-";
}

// Any member reads; connecting and changing connections is an admin's.
const SHOW_BALANCES_KEY = "bank-connections:show-balances";

export function readShowBalances(): boolean {
  try {
    return localStorage.getItem(SHOW_BALANCES_KEY) === "1";
  } catch {
    return false;
  }
}

export function BankConnectionsPage({ onOpenAccount }: { onOpenAccount: (accountId: string) => void }) {
  const api = useDolfinClient();
  const { can, roleAllows } = usePermissions();
  const canManage = can("connectBank");
  // Seeing balances is a read, so it follows the role, not the write gate (a demo organisation's admin sees them).
  const canSeeBalances = roleAllows("connectBank");
  const isDemo = useIsDemo();
  const [connections, setConnections] = useState<BankConnection[]>([]);
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [connectOpen, setConnectOpen] = useState(false);
  // Per browser, and only an admin's to turn on: members never see balances.
  const [showBalances, setShowBalances] = useState(readShowBalances);
  const balancesVisible = canSeeBalances && showBalances;
  const [busyId, setBusyId] = useState<string | null>(null);
  const [pendingDisconnect, setPendingDisconnect] = useState<BankConnection | null>(null);
  const [pendingReauthorise, setPendingReauthorise] = useState<
    { connection: BankConnection; authorisation: BankConnectionAuthorisation } | null
  >(null);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const [connectionsResult, accountsResult] = await Promise.all([
        api.GET("/v1/bank-connections"),
        api.GET("/v1/bank-accounts"),
      ]);
      if (connectionsResult.error || accountsResult.error) {
        setLoadError(apiErrorMessage(connectionsResult.error ?? accountsResult.error, "Failed to load bank connections"));
        return;
      }
      setConnections(connectionsResult.data?.data ?? []);
      setAccounts(accountsResult.data?.data ?? []);
    } catch {
      setLoadError("Failed to load bank connections");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    load();
  }, [load]);

  async function reconfirm(connection: BankConnection) {
    setBusyId(connection.id);
    const { data, error } = await api.POST("/v1/bank-connections/{id}/reconfirm", {
      params: { path: { id: connection.id } },
    });
    if (error) {
      toast.error(apiErrorMessage(error, "Couldn't reconfirm the connection."));
    } else if (data?.state === "Reauthorising") {
      toast.message("Your bank needs you to reauthorise this connection instead.");
    } else {
      toast.success(`Access confirmed until ${formatDate(data?.reconfirmBy, "long")}.`);
    }
    await load();
    setBusyId(null);
  }

  async function reauthorise(connection: BankConnection) {
    setBusyId(connection.id);
    const { data, error } = await api.POST("/v1/bank-connections/{id}/reauthorise", {
      params: { path: { id: connection.id } },
      body: { returnUrl: bankConnectionReturnUrl() },
    });
    if (error || !data?.authorisationUrl) {
      toast.error(apiErrorMessage(error, "Couldn't start reauthorising the connection."));
      setBusyId(null);
      return;
    }
    // The Yapily Connect disclosure must be shown on the step before the holder goes to the bank.
    setPendingReauthorise({ connection, authorisation: data });
    setBusyId(null);
  }

  async function disconnect(connection: BankConnection) {
    setBusyId(connection.id);
    const { error } = await api.DELETE("/v1/bank-connections/{id}", { params: { path: { id: connection.id } } });
    if (error) {
      toast.error(apiErrorMessage(error, "Couldn't disconnect the bank."));
    } else {
      toast.success(`${connection.institutionName} disconnected.`);
    }
    setPendingDisconnect(null);
    await load();
    setBusyId(null);
  }

  function toggleBalances(on: boolean) {
    setShowBalances(on);
    try {
      localStorage.setItem(SHOW_BALANCES_KEY, on ? "1" : "0");
    } catch {
      // storage unavailable: the choice lasts for this visit only
    }
  }

  const balancesSwitch = (
    <ToggleChip
      id="show-balances"
      icon={Eye}
      label="Show balances"
      checked={balancesVisible}
      onCheckedChange={toggleBalances}
      disabled={!canSeeBalances}
      hint={
        canSeeBalances
          ? "Adds each account's latest balance from the bank. Remembered in this browser."
          : "Only an organisation admin or owner can show balances."
      }
    />
  );

  const connectButton = (
    <Button size="sm" disabled={!canManage} onClick={() => setConnectOpen(true)}>
      <Plus className="size-4" /> Connect a bank
    </Button>
  );

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Organisation"
        title="Bank connections"
        subtitle="Connect your business bank accounts read-only to see their balances and transactions here. Nothing can be paid from a connection, and you can disconnect at any time."
        actions={
          <>
            {balancesSwitch}
            {connectButton}
          </>
        }
      />
      {!canManage ? (
        <p className="-mt-2 mb-5 text-xs text-muted-foreground">
          {isDemo
            ? "These are demo connections with made-up data, so they can't be changed."
            : "Only an organisation admin can connect or change banks."}
        </p>
      ) : null}

      {loading ? (
        <LoadingState label="Loading bank connections..." />
      ) : loadError ? (
        <ErrorState message={loadError} onRetry={load} />
      ) : connections.length === 0 ? (
        <EmptyState icon={Landmark} title="No banks connected yet" action={canManage ? connectButton : undefined}>
          Connect a bank and its accounts appear here with their latest balances, refreshed every day.
        </EmptyState>
      ) : (
        <div className="space-y-8">
          <section className="space-y-3">
            <Eyebrow>Accounts</Eyebrow>
            {accounts.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No accounts yet. They appear once a connection is authorised at the bank.
              </p>
            ) : (
              <TableShell>
                <Table>
                  <TableHeader>
                    <HeaderRow>
                      <TableHead className={TH}>Account</TableHead>
                      <TableHead className={TH}>Sort code and number</TableHead>
                      {balancesVisible ? <TableHead className={cn(TH, "text-right")}>Balance</TableHead> : null}
                    </HeaderRow>
                  </TableHeader>
                  <TableBody>
                    {accounts.map((account) => (
                      <TableRow key={account.id} {...rowLink(() => onOpenAccount(account.id))}>
                        <TableCell>
                          <div className="font-semibold">{account.name}</div>
                          <div className="text-xs text-muted-foreground">
                            {[
                              connections.find((c) => c.id === account.bankConnectionId)?.institutionName,
                              account.type,
                            ]
                              .filter(Boolean)
                              .join(" · ")}
                          </div>
                        </TableCell>
                        <TableCell className="font-mono text-xs whitespace-pre">{accountIdentifier(account)}</TableCell>
                        {balancesVisible ? (
                          <TableCell className="text-right">
                            <div className="font-semibold tabular-nums">
                              {account.balance != null ? formatCurrency(account.balance, account.currency) : "-"}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {account.connectionState !== "Connected"
                                ? "Not refreshing"
                                : account.balanceAsOf
                                  ? `As of ${formatDateTime(account.balanceAsOf)}`
                                  : null}
                            </div>
                          </TableCell>
                        ) : null}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableShell>
            )}
          </section>

          <section className="space-y-3">
            <Eyebrow>Connections</Eyebrow>
            <ul className="divide-y rounded-xl border bg-card/60">
              {connections.map((connection) => (
                <ConnectionRow
                  key={connection.id}
                  connection={connection}
                  canManage={canManage}
                  busy={busyId === connection.id}
                  onReconfirm={() => reconfirm(connection)}
                  onReauthorise={() => reauthorise(connection)}
                  onDisconnect={() => setPendingDisconnect(connection)}
                />
              ))}
            </ul>
          </section>
        </div>
      )}

      <ConnectBankDialog open={connectOpen} onOpenChange={setConnectOpen} />

      <AlertDialog open={pendingReauthorise !== null} onOpenChange={(open) => !open && setPendingReauthorise(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reauthorise {pendingReauthorise?.connection.institutionName}?</AlertDialogTitle>
            <AlertDialogDescription>
              You&apos;ll be securely redirected to your bank to approve read-only access again. Nothing can be paid
              from this connection.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <DisclosureNotice disclosure={pendingReauthorise?.authorisation.disclosure} />
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                if (pendingReauthorise) window.location.assign(pendingReauthorise.authorisation.authorisationUrl);
              }}
            >
              <ExternalLink className="size-4" />
              Continue to your bank
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={pendingDisconnect !== null} onOpenChange={(open) => !open && busyId === null && setPendingDisconnect(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect {pendingDisconnect?.institutionName}?</AlertDialogTitle>
            <AlertDialogDescription>
              Dolfin stops reading these accounts and withdraws its access at the bank. Their balances drop off this
              page. You can connect the bank again at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busyId !== null}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                if (pendingDisconnect) disconnect(pendingDisconnect);
              }}
              disabled={busyId !== null}
              variant="destructive"
            >
              {busyId !== null ? <Spinner className="size-4" /> : null}
              Disconnect
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  );
}

function ConnectionRow({
  connection,
  canManage,
  busy,
  onReconfirm,
  onReauthorise,
  onDisconnect,
}: {
  connection: BankConnection;
  canManage: boolean;
  busy: boolean;
  onReconfirm: () => void;
  onReauthorise: () => void;
  onDisconnect: () => void;
}) {
  const reconfirmIn = daysUntil(connection.reconfirmBy);
  const reconfirmDue = connection.state === "Connected" && reconfirmIn !== null && reconfirmIn <= RECONFIRM_WINDOW_DAYS;
  const reauthorisable = connection.state === "Expired" || connection.state === "Reauthorising";
  const failure = bankConnectionFailureLabel(connection.failureReason);

  let detail: React.ReactNode;
  if (connection.state === "Connected") {
    detail = reconfirmDue ? (
      <span className="flex items-center gap-1 text-amber-700 dark:text-amber-300">
        <Clock className="size-3.5" /> Reconfirm access by {formatDate(connection.reconfirmBy, "long")}
      </span>
    ) : (
      <span className="flex items-center gap-1">
        <CircleCheck className="size-3.5 text-emerald-600" />
        Connected {formatDate(connection.connectedAt)}
        {connection.reconfirmBy ? ` · reconfirm by ${formatDate(connection.reconfirmBy)}` : null}
      </span>
    );
  } else if (connection.state === "Failed" || connection.state === "Revoked" || connection.state === "Expired") {
    detail = (
      <span className="flex items-center gap-1">
        <AlertTriangle className="size-3.5 text-destructive" />
        {connection.state === "Expired"
          ? "Access lapsed. Reauthorise at the bank to start reading the accounts again."
          : connection.state === "Revoked"
            ? "Access was withdrawn at the bank. Connect it again to restore it."
            : (failure ?? "The connection was not completed.")}
      </span>
    );
  } else if (connection.state === "AwaitingFurtherAuthorisation") {
    detail = "Another holder of the account must approve access in their own banking app.";
  } else if (connection.state === "Disconnected") {
    detail = `Disconnected ${formatDate(connection.updatedAt)}`;
  } else {
    detail = `Started ${formatDateTime(connection.createdAt)}`;
  }

  return (
    <li className="flex flex-col gap-3 p-4 @2xl:flex-row @2xl:items-center @2xl:justify-between">
      <div className="min-w-0 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold">{connection.institutionName}</span>
          <BankConnectionStateBadge state={connection.state} />
          {connection.accounts.length > 0 ? (
            <span className="text-xs text-muted-foreground">
              {connection.accounts.length} account{connection.accounts.length === 1 ? "" : "s"}
            </span>
          ) : null}
        </div>
        <div className="text-xs text-muted-foreground">{detail}</div>
      </div>
      {canManage && !TERMINAL.includes(connection.state) ? (
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {reconfirmDue ? (
            <Button size="sm" variant="outline" disabled={busy} onClick={onReconfirm}>
              {busy ? <Spinner className="size-4" /> : <RefreshCw className="size-4" />} Reconfirm
            </Button>
          ) : null}
          {reauthorisable ? (
            <Button size="sm" variant="outline" disabled={busy} onClick={onReauthorise}>
              {busy ? <Spinner className="size-4" /> : <ExternalLink className="size-4" />} Reauthorise
            </Button>
          ) : null}
          <Button size="sm" variant="ghost" disabled={busy} onClick={onDisconnect}>
            <Unplug className="size-4" /> Disconnect
          </Button>
        </div>
      ) : null}
    </li>
  );
}
